/**
 * Recipe Rating React Query Hooks (Task 18, "Made it?" thumbs)
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useRef } from "react";
import type { RecommendationOutcomeContext } from "@batch-and-bowl/shared";
import { toast } from "sonner";
import * as ratingsApi from "../api/ratings";
import { MEAL_PLANS_QUERY_KEY } from "./use-meal-plans";
import { extractErrorMessage, extractErrorStatus } from "../errors";
import { ApiError } from "../api/client";
import { useRecommendationFeedback } from "../providers/recommendation-feedback-provider";
import { hasRecipe, type MealPlan, type MealSlot, type RatingValue } from "../api/types";

export interface RateRecipeVariables {
  recipeId: string;
  value: RatingValue | null;
  outcome?: RecommendationOutcomeContext;
  expectedActorUserId?: string;
}

/**
 * Returns a copy of `data` (a meal-plans list or a single meal plan --
 * useMealPlans() and useMealPlan(id) share the "meal-plans" query key
 * prefix but differ in shape) with `userRating` set to `value` on every
 * meal slot whose recipe matches `recipeId`. Leaves everything else
 * untouched, including slots for other recipes.
 */
function applyUserRating<T extends MealPlan | MealPlan[]>(
  data: T,
  recipeId: string,
  value: RatingValue | null,
  expectedValue?: RatingValue | null,
): T {
  const updateSlot = (slot: MealSlot): MealSlot =>
    hasRecipe(slot) && slot.recipe.id === recipeId && (expectedValue === undefined || slot.recipe.userRating === expectedValue)
      ? { ...slot, recipe: { ...slot.recipe, userRating: value } }
      : slot;

  if (Array.isArray(data)) {
    return data.map((plan) => ({
      ...plan,
      mealSlots: plan.mealSlots.map(updateSlot),
    })) as T;
  }

  return {
    ...data,
    mealSlots: data.mealSlots.map(updateSlot),
  } as T;
}

/**
 * Set (up/down) or clear (null) the caller's thumbs rating on a recipe.
 *
 * Optimistic update with rollback, structurally copied from
 * useToggleFavorite (use-recipes.ts): snapshot every affected query before
 * mutating, write the optimistic value in, and restore the snapshot on
 * error.
 *
 * There is no dedicated ratings query key -- MealPlanner reads
 * meal.recipe.userRating straight off useMealPlans()'s cache (see
 * GET /meal-plans' server-side batch enrichment in
 * apps/api/src/routes/meal-plans.ts), so the meal-plans cache IS where the
 * UI's rating state lives. This walks every cached query under the
 * "meal-plans" key prefix (the list AND any per-id useMealPlan(id)
 * queries) and updates whichever ones reference the rated recipe.
 */
export function useRateRecipe() {
  const queryClient = useQueryClient();
  const feedback = useRecommendationFeedback();
  const actorRef = useRef(feedback); actorRef.current = feedback;
  const operations = useRef(new WeakMap<RateRecipeVariables, RateRecipeVariables>());
  const sequence = useRef(0), latest = useRef(new Map<string, number>());
  const currentActor = (actor?: string) => actorRef.current?.isCurrentActor?.() !== false && (!actor || actor === actorRef.current?.actorUserId);
  const capture = (variables: RateRecipeVariables) => {
    let operation = operations.current.get(variables);
    if (!operation) {
      operation = { ...variables, outcome: variables.outcome ? { ...variables.outcome } : undefined,
        expectedActorUserId: variables.expectedActorUserId ?? actorRef.current?.actorUserId };
      operations.current.set(variables, operation);
    }
    return operation;
  };

  return useMutation({
    mutationFn: async (variables: RateRecipeVariables) => {
      const operation = capture(variables);
      for (let attempt = 0; ; attempt += 1) {
        if (!currentActor(operation.expectedActorUserId)) throw new ApiError("Signed-in account changed", 409);
        try {
          return operation.outcome || operation.expectedActorUserId
            ? await ratingsApi.rateRecipe(operation.recipeId, operation.value, operation.outcome, operation.expectedActorUserId)
            : await ratingsApi.rateRecipe(operation.recipeId, operation.value);
        } catch (error) {
          const status = extractErrorStatus(error);
          if (!operation.outcome || attempt >= 2 || !(status === 0 || status === 408 || status === 429 || status >= 500)) throw error;
          await new Promise(resolve => setTimeout(resolve, 150 * (attempt + 1)));
        }
      }
    },
    onMutate: async (variables) => {
      const operation = capture(variables), { recipeId, value } = operation;
      if (!currentActor(operation.expectedActorUserId)) throw new ApiError("Signed-in account changed", 409);
      const revision = ++sequence.current; latest.current.set(recipeId, revision);
      await queryClient.cancelQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
      if (!currentActor(operation.expectedActorUserId)) throw new ApiError("Signed-in account changed", 409);

      const previousQueries = queryClient.getQueriesData<MealPlan | MealPlan[]>(
        { queryKey: [MEAL_PLANS_QUERY_KEY] },
      );

      previousQueries.forEach(([queryKey, data]) => {
        if (!data) return;
        queryClient.setQueryData(
          queryKey as QueryKey,
          applyUserRating(data, recipeId, value),
        );
      });

      return { previousQueries, operation, revision };
    },
    onSuccess: (data, _variables, context) => {
      if (!context || !currentActor(context.operation.expectedActorUserId) || latest.current.get(context.operation.recipeId) !== context.revision) return;
      // An old operation replay may return a newer rating. Use that authoritative value.
      for (const [queryKey, cached] of queryClient.getQueriesData<MealPlan | MealPlan[]>({ queryKey: [MEAL_PLANS_QUERY_KEY] })) {
        if (cached) queryClient.setQueryData(queryKey, applyUserRating(cached, context.operation.recipeId, data.value));
      }
    },
    onError: (err, _variables, context) => {
      if (!context || !currentActor(context.operation.expectedActorUserId) || latest.current.get(context.operation.recipeId) !== context.revision) return;
      context.previousQueries.forEach(([queryKey, previous]) => {
        if (!previous) return;
        const oldPlans = Array.isArray(previous) ? previous : [previous];
        const oldSlot = oldPlans.flatMap(plan => plan.mealSlots).find(slot => hasRecipe(slot) && slot.recipe.id === context.operation.recipeId);
        const current = queryClient.getQueryData<MealPlan | MealPlan[]>(queryKey);
        if (!current || !oldSlot || !hasRecipe(oldSlot)) return;
        // Restore this field only while it still holds our optimistic value.
        // Whole-query rollback would erase independently saved slots or edits.
        queryClient.setQueryData(queryKey, applyUserRating(current, context.operation.recipeId, oldSlot.recipe.userRating ?? null, context.operation.value));
      });
      // Matches the sibling swap/add-meal mutations (MealPlanner.tsx,
      // use-meal-plans.ts): a silent rollback reads as "it worked" to the
      // user, who has no way to tell their rating didn't save.
      toast.error(extractErrorMessage(err, "Failed to save rating. Please try again."));
    },
    onSettled: (_data, _error, _variables, context) => {
      if (!context || !currentActor(context.operation.expectedActorUserId) || latest.current.get(context.operation.recipeId) !== context.revision) return;
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}
